/**
 * CrossReportSynthesisSection (crossReportSynthesis) — per-domain synthesis
 * blocks aggregated across the site's source reports. W2c editorial port: the
 * hand-rolled accent-left-rule blocks (border + h3 + roleColor'd <p>) move
 * into EditorialSubsection — each domain block is a FINDING (label = domain,
 * body = synthesis prose), the findings-led Study-B treatment — so the chrome
 * follows the preset (journal hairline / institutional left rule / compact).
 * The synthesis text, the [n] claim walking, the "Sources: …" provenance
 * footer, and the empty-section semantics are unchanged.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialSubsection } from '../../report/editorial';
import { ReportSourceNote } from '../../report/ReportSourceNote';

interface Props {
  report: SiteRagReport;
  label: string;
}

export const CrossReportSynthesisSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const blocks = report.crossReportSynthesis ?? [];
  return (
    <SectionShell label={label} hasData={blocks.length > 0} report={report} sectionId="crossReportSynthesis">
      <div className="space-y-4">
        {blocks.map((block, i) => (
          <EditorialSubsection key={i} theme={theme} label={block.domain}>
            <p className="m-0">
              {walkClaimsText(block.synthesis, report.references, report.claims)}
            </p>
            {block.sourceReportTypes.length > 0 && (
              <ReportSourceNote className="mt-1 mb-0">
                Sources: {block.sourceReportTypes.join(', ')}
              </ReportSourceNote>
            )}
          </EditorialSubsection>
        ))}
      </div>
    </SectionShell>
  );
};
